<template>
	<div class="patchingRoot">
		<p class="hintText">
			Windows updates are installed on a database server by the DbServerAgent running on it: it searches Windows Update, installs the
			selected updates one at a time, and stops SQL Server and reboots whenever an update needs it.  A server can only be patched while
			the web apps are <strong>not</strong> using it and no maintenance operation involves it — and while a server is patching, no
			migration or cutover involving it can start.
		</p>
		<div v-if="!patching" class="mutedText">The patching status is not available.</div>
		<template v-else>
			<div v-if="patching.dryRun" class="noteBox">
				Dry-run mode: a job started here is <strong>simulated</strong>.  No server is contacted and nothing is installed.
			</div>
			<div v-if="patching.stub" class="noteBox">
				Dry-run stub mode: the readiness facts below are synthetic, not read from the real servers.
			</div>

			<div class="patchGrid">
				<div v-for="s in servers" :key="s.role + s.name" class="patchCard" :class="{ patchCardBusy: jobActive(s), patchCardActiveDb: s.isActive }">
					<div class="patchHead">
						<span class="roleTag">{{s.role}}</span>
						<span class="serverName">{{s.name || '(not configured)'}}</span>
						<span v-if="s.isActive" class="stateBadge badgeActive" title="The web apps are effectively connected to this server, so it cannot be patched.">ACTIVE — in use by the web apps</span>
						<span v-if="s.manualJob && s.manualJob.active" class="stateBadge badgeInfo" title="A manual patch job started from this page is in progress.">PATCHING</span>
						<span v-else-if="s.agentJob && !s.agentJob.terminal" class="stateBadge badgeWarn" title="DbServerAgent reports a patch job that this page did not start (or one that outlived its tracking).">agent job running</span>
					</div>
					<div v-if="s.host && s.host !== s.name" class="mutedText hostLine">{{s.host}}</div>

					<!-- ==== Agent readiness ==================================================================== -->
					<div class="kvGrid">
						<span class="kvKey">DbServerAgent</span>
						<span>
							<span class="statusDot" :class="agentDotClass(s.agent)" :title="agentDotTitle(s.agent)"></span>
							<template v-if="!s.agent">no probe result</template>
							<template v-else-if="s.agent.probing">probing…</template>
							<template v-else-if="!s.agent.reachable"><span class="problemText">unreachable</span><template v-if="s.agent.error">: {{s.agent.error}}</template></template>
							<template v-else-if="s.agent.refused || !s.agent.contractOk">
								<span class="problemText">{{s.agent.refused ? 'refused the signed request' : 'speaks patch contract v' + s.agent.contractVersion}}</span><template v-if="s.agent.error">: {{s.agent.error}}</template>
							</template>
							<template v-else>
								ready<template v-if="s.agent.agentVersion"> — agent {{s.agent.agentVersion}}</template><template v-if="s.agent.latencyMs != null">, {{s.agent.latencyMs}} ms</template>
								<span v-if="s.agent.stale" class="mutedText"> (cached)</span>
								<span v-if="s.agent.stub" class="stateBadge badgeOff">stub</span>
							</template>
						</span>
						<template v-if="s.agent && s.agent.osVersion">
							<span class="kvKey">Windows</span>
							<span>{{s.agent.osVersion}}<template v-if="s.agent.lastBootUtc"> — last booted {{formatDateTime(s.agent.lastBootUtc)}}</template></span>
						</template>
						<template v-if="s.agent && s.agent.sqlServiceName">
							<span class="kvKey">SQL Server service</span>
							<span>
								<code class="inline">{{s.agent.sqlServiceName}}</code>
								<span :class="{ problemText: s.agent.sqlServiceStatus && s.agent.sqlServiceStatus !== 'Running' }">{{s.agent.sqlServiceStatus || 'unknown'}}</span>
							</span>
						</template>
						<template v-if="s.agent && (s.agent.rebootPending || s.agent.installerBusy || s.agent.clockSkewSeconds >= 10)">
							<span class="kvKey">Notices</span>
							<span>
								<span v-if="s.agent.rebootPending" class="stateBadge badgeWarn" :title="rebootPendingTitle(s.agent)">reboot pending</span>
								<span v-if="s.agent.installerBusy" class="stateBadge badgeBad" title="The Windows Update installer reports an installation in progress, started outside this page.">installer busy</span>
								<span v-if="s.agent.clockSkewSeconds >= 10" class="stateBadge badgeWarn" :title="'The signed request tokens allow 60 s of clock difference.'">clock off by {{s.agent.clockSkewSeconds}} s</span>
							</span>
						</template>
						<span class="kvKey">Updates installed here</span>
						<span class="mutedText">{{s.canaryNote}}</span>
					</div>

					<!-- ==== The job in progress / the last one ================================================= -->
					<div v-if="s.agentJob || s.manualJob" class="jobBlock" :class="{ jobActive: jobActive(s) }">
						<div class="jobHead">
							<template v-if="s.manualJob">
								<span class="stateBadge" :class="patchStatusBadgeClass(s.manualJob.status)">{{patchStatusLabel(s.manualJob.status)}}</span>
								<span class="jobTitle">
									started by {{s.manualJob.actor || '?'}} {{formatDateTime(s.manualJob.startedUtc)}}
									<span class="mutedText"> — {{formatElapsed(s.manualJob.elapsedMs)}} {{s.manualJob.active ? 'so far' : 'total'}}</span>
								</span>
								<span v-if="s.manualJob.dryRun" class="stateBadge badgeOff" title="Simulated in dry-run mode; no server was contacted.">simulated</span>
							</template>
							<template v-else>
								<span class="stateBadge" :class="patchPhaseBadgeClass(s.agentJob.phase)">{{s.agentJob.terminal ? 'last agent job' : 'agent job'}}</span>
								<span class="jobTitle">{{s.agentJob.jobId}}<span class="mutedText"> — started {{formatDateTime(s.agentJob.startedUtc)}} by DbServerAgent itself</span></span>
							</template>
						</div>

						<div v-if="s.agentJob" class="kvGrid">
							<span class="kvKey">Doing now</span>
							<span>
								<span class="stateBadge" :class="patchPhaseBadgeClass(s.agentJob.phase)">{{patchPhaseLabel(s.agentJob.phase)}}</span>
								<template v-if="s.agentJob.pass"> pass {{s.agentJob.pass}} of {{Math.max(s.agentJob.pass, s.agentJob.maxPasses)}}</template>
								<template v-if="s.agentJob.currentActivity"> — {{s.agentJob.currentActivity}}</template>
							</span>
							<span class="kvKey">Updates</span>
							<span>
								<strong>{{s.agentJob.updatesInstalled}}</strong> installed<template v-if="s.agentJob.updatesFailed">, <strong class="problemText">{{s.agentJob.updatesFailed}}</strong> failed</template><template v-if="s.agentJob.updatesSkipped">, {{s.agentJob.updatesSkipped}} skipped</template><template v-if="s.agentJob.updatesDeferred">, {{s.agentJob.updatesDeferred}} deferred</template>
								of {{s.agentJob.updatesFound}} applicable<template v-if="s.agentJob.rebootCount"> · {{s.agentJob.rebootCount}} reboot{{s.agentJob.rebootCount === 1 ? '' : 's'}}</template>
								<template v-if="s.agentJob.remainingApplicable != null"> · <span :class="remainingClass(s.agentJob)" :title="remainingTitle(s.agentJob)">{{remainingText(s.agentJob)}}</span></template>
							</span>
							<template v-if="s.agentJob.error">
								<span class="kvKey">Agent error</span>
								<span class="problemText">{{s.agentJob.error}}</span>
							</template>
						</div>

						<div v-if="s.manualJob && s.manualJob.active && s.manualJob.progress" class="progressLine">
							<span class="pulseDot"></span> {{s.manualJob.progress}}
						</div>
						<div v-if="s.manualJob && s.manualJob.active && deadline(s.manualJob)" class="mutedText deadlineLine">
							Tracking deadline {{deadline(s.manualJob).text}}<span v-if="deadline(s.manualJob).passed"> — DbServerAgent finishes the job on its own; the lockout stays until it reports it done.</span>
						</div>
						<div v-if="s.manualJob && s.manualJob.summary && !s.manualJob.active" class="summaryLine" :class="{ problemText: s.manualJob.failed }">{{s.manualJob.summary}}</div>
						<div v-if="s.manualJob && s.manualJob.error && !s.manualJob.active" class="errorBox">{{s.manualJob.error}}</div>
						<ul v-if="s.manualJob && s.manualJob.warnings && s.manualJob.warnings.length" class="warningList">
							<li v-for="(w, i) in s.manualJob.warnings" :key="i">{{w}}</li>
						</ul>

						<div v-if="updatesOf(s).length" class="updatesBlock">
							<button type="button" class="linkButton" @click="toggleUpdates(s.name)">
								{{expanded[s.name] ? 'Hide' : 'Show'}} the {{updatesOf(s).length}} update{{updatesOf(s).length === 1 ? '' : 's'}}
							</button>
							<div v-if="expanded[s.name]" class="tableScroll">
								<table class="updateTable">
									<thead>
										<tr><th>Result</th><th>KB</th><th>Update</th><th>Pass</th></tr>
									</thead>
									<tbody>
										<tr v-for="u in updatesOf(s)" :key="u.updateId || u.title">
											<td class="nowrap" :class="patchOutcomeClass(u.outcome)">{{patchOutcomeLabel(u.outcome)}}</td>
											<td class="nowrap">{{formatKbs(u.kbs, u.updateId)}}</td>
											<td>
												{{u.title}}
												<div v-if="u.detail" class="mutedText updateDetail">{{u.detail}}</div>
											</td>
											<td class="nowrap">{{u.pass || '—'}}</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>
						<div v-if="s.agentJob && s.agentJob.logLocations && s.agentJob.logLocations.length" class="mutedText logLine">
							Detail on the server: {{s.agentJob.logLocations.join(' · ')}}
						</div>
					</div>

					<!-- ==== Start control ====================================================================== -->
					<div class="startBlock">
						<template v-if="permissionWrite">
							<div v-if="busyServer === s.name" class="loadingInline"><ScaleLoader /> Starting the patch job on {{s.name}}…</div>
							<template v-else-if="s.canStart">
								<div class="slideRed">
									<slide-button :key="'slide_patch_' + s.name + '_' + slideCounter"
												  :auto-width="false"
												  :circle="true"
												  :noanimate="true"
												  :width="320"
												  :height="50"
												  :text="'slide to patch ' + s.name"
												  success-text="starting"
												  :disabled="disabled"
												  :requireReleaseToComplete="true"
												  @completed="$emit('start', s.name)" />
								</div>
								<div class="startNote">
									Opens a confirmation listing exactly what will happen.  {{s.name}} will reboot as often as the updates require, and
									no migration or cutover involving it can start until the job finishes.
								</div>
							</template>
							<div v-else class="disabledReason">{{s.startBlockedReason || 'Patching is not available on this server right now.'}}</div>
						</template>
						<div v-else class="mutedText">
							{{s.canStart ? 'This server could be patched, but you do not have the Online Server Maintenance Write permission.' : (s.startBlockedReason || '')}}
						</div>
					</div>
				</div>
			</div>

			<div v-if="patching.policy" class="policyLine mutedText">
				Policy (Admin → Settings): {{policyText}}
			</div>
		</template>
	</div>
</template>

<script>
	// The SQL Server Maintenance page's "Windows patching" section: per database server, DbServerAgent's readiness, the patch job it
	// is running (or last ran), the manual job this service tracks, and the start control.  Purely presentational — the parent owns
	// the data (DbMaintenancePatchingStatus) and performs the start through the confirmation dialog.
	import SlideButton from 'appRoot/vues/common/controls/SlideButton.vue';
	import
		{
			formatDateTime, formatElapsed, formatCountdown, patchStatusLabel, patchStatusBadgeClass, patchPhaseLabel,
			patchPhaseBadgeClass, patchOutcomeClass, patchOutcomeLabel, formatKbs
		} from './runFormat';

	export default {
		components: { SlideButton },
		emits: ['start'],
		props:
		{
			/** DbMaintenancePatchingStatus, or null while it is unavailable. */
			patching: {
				type: Object,
				default: null
			},
			permissionWrite: {
				type: Boolean,
				default: false
			},
			/** Name of the server whose start is in flight, or null. */
			busyServer: {
				type: String,
				default: null
			},
			/** True while any other page action is in flight (slide controls are then disabled). */
			disabled: {
				type: Boolean,
				default: false
			},
			/** Bumped by the parent to reset the slide controls. */
			slideCounter: {
				type: Number,
				default: 0
			},
			/** The parent's one-second clock, so countdowns tick. */
			now: {
				type: Number,
				default: 0
			}
		},
		data()
		{
			return {
				expanded: {} // server name -> true while its update list is expanded
			};
		},
		computed:
		{
			servers()
			{
				return this.patching && Array.isArray(this.patching.servers) ? this.patching.servers : [];
			},
			policyText()
			{
				let p = this.patching.policy;
				let parts = [];
				parts.push('source ' + (p.updateService || '?'));
				parts.push('up to ' + p.maxPasses + ' pass' + (p.maxPasses === 1 ? '' : 'es'));
				parts.push('tracked for ' + p.timeoutMinutes + ' min');
				parts.push(p.canaryFilter ? 'canary filter on (' + p.canaryHistoryDays + ' days of history)' : 'canary filter OFF');
				if (!p.includeDefinitionUpdates)
					parts.push('definition updates skipped');
				if (p.minimumAgeDays > 0)
					parts.push('minimum age ' + p.minimumAgeDays + ' d');
				if (p.excludedKbs && p.excludedKbs.length)
					parts.push('never: ' + p.excludedKbs.join(', '));
				return parts.join(' · ') + '.';
			}
		},
		methods:
		{
			formatDateTime,
			formatElapsed,
			patchStatusLabel,
			patchStatusBadgeClass,
			patchPhaseLabel,
			patchPhaseBadgeClass,
			patchOutcomeClass,
			patchOutcomeLabel,
			formatKbs,
			jobActive(s)
			{
				return !!(s.manualJob && s.manualJob.active) || !!(s.agentJob && !s.agentJob.terminal);
			},
			/** The per-update list: the agent's own (richer: skip reasons, passes), which is what both jobs report. */
			updatesOf(s)
			{
				return s.agentJob && Array.isArray(s.agentJob.updates) ? s.agentJob.updates : [];
			},
			toggleUpdates(name)
			{
				this.expanded[name] = !this.expanded[name];
			},
			/**
			 * What the agent's final scan says about the server, in a few words.  remainingApplicable counts everything that is
			 * still applicable, remainingSelectable how much of it the rules would install, and the interesting case is
			 * "applicable but none selectable": the server stays behind until the canary catches up or a rule changes, and it must
			 * not read like a finished job.  An agent too old to send remainingSelectable sends the post-rule count only, so its
			 * zero cannot be presented as "nothing remains".
			 *
			 * remainingHeldBack / remainingNeverInstall split "not selected" into the two things it really means: waiting for the
			 * canary or the minimum age (a later job installs it — the server IS behind) versus a decision the policy will not
			 * revisit (a definition update, an excluded KB — offered by every scan for ever, and no cause for alarm).  An agent too
			 * old to classify them leaves both null, and heldBack() then falls back to the old, deliberately pessimistic reading.
			 */
			heldBackCount(job)
			{
				if (job.remainingHeldBack != null)
					return job.remainingHeldBack;
				if (job.remainingSelectable == null)
					return null;
				return Math.max(0, job.remainingApplicable - job.remainingSelectable - (job.remainingNeverInstall || 0));
			},
			remainingText(job)
			{
				if (job.remainingSelectable == null)
					return job.remainingApplicable === 0 ? 'nothing further was selectable' : job.remainingApplicable + ' left for a later job';
				if (job.remainingApplicable === 0)
					return 'nothing applicable remains';
				let held = this.heldBackCount(job);
				if (job.remainingSelectable === 0 && held === 0)
					return job.remainingApplicable + ' applicable, none the policy installs';
				if (job.remainingSelectable === 0)
					return held + ' of ' + job.remainingApplicable + ' still applicable are held back';
				return job.remainingApplicable + ' still applicable';
			},
			remainingClass(job)
			{
				if (job.remainingSelectable === 0 && this.heldBackCount(job) > 0)
					return 'problemText';
				if (job.remainingSelectable === 0 && this.heldBackCount(job) === 0)
					return 'mutedText';
				return job.remainingApplicable === 0 && job.remainingSelectable != null ? 'mutedText' : '';
			},
			remainingTitle(job)
			{
				if (job.remainingSelectable == null)
					return 'This DbServerAgent reports only what its selection rules would still install, not how many updates remain applicable in total.';
				let held = this.heldBackCount(job);
				if (job.remainingSelectable === 0 && held === 0)
					return 'Everything still applicable is something the current policy never installs (a definition update, or a KB on the exclusion list).  Those are offered by every scan for ever; this server is not behind.';
				if (job.remainingSelectable === 0)
					return held + ' update(s) are held back for now by the canary filter or the minimum age.  This server is not fully patched; a later job installs them once the canary has them or they are old enough.';
				return 'Updates the final scan still found applicable; ' + job.remainingSelectable + ' of them may be installed under the current rules.';
			},
			deadline(job)
			{
				return formatCountdown(job.deadlineUtc, this.now || Date.now());
			},
			agentDotClass(a)
			{
				if (!a || a.probing)
					return 'dotGray';
				if (!a.reachable || a.refused || !a.contractOk)
					return 'dotRed';
				if (a.installerBusy || a.rebootPending)
					return 'dotAmber';
				return 'dotGreen';
			},
			agentDotTitle(a)
			{
				if (!a || a.probing)
					return 'Probing DbServerAgent';
				if (!a.reachable)
					return 'DbServerAgent is unreachable';
				if (a.refused)
					return 'DbServerAgent refused the signed patch-control request';
				if (!a.contractOk)
					return 'DbServerAgent speaks a different patch-control contract version';
				if (a.installerBusy)
					return 'An installation is already in progress on this server';
				if (a.rebootPending)
					return 'Windows reports a pending reboot';
				return 'Ready to patch';
			},
			rebootPendingTitle(a)
			{
				let sources = [];
				if (a.rebootPendingCbs)
					sources.push('Component Based Servicing');
				if (a.rebootPendingWindowsUpdate)
					sources.push('Windows Update');
				if (a.rebootPendingWua)
					sources.push('the Windows Update Agent');
				return 'A reboot is pending' + (sources.length ? ' (' + sources.join(', ') + ')' : '') + '.  A patch job performs it.';
			}
		}
	}
</script>

<style scoped>
	.hintText
	{
		color: #555555;
		font-size: 0.92em;
		margin: 0 0 10px 0;
	}

	.mutedText
	{
		color: #888888;
	}

	.problemText
	{
		color: #8A0000;
		font-weight: bold;
	}

	.noteBox
	{
		background-color: #F4F4F4;
		border: 1px solid #DDDDDD;
		color: #555555;
		padding: 8px 12px;
		margin: 0 0 10px 0;
	}

	.errorBox
	{
		background-color: #FFE0E0;
		border: 1px solid #C00000;
		color: #660000;
		padding: 8px 12px;
		margin: 8px 0;
		white-space: pre-wrap;
		word-break: break-word;
	}

	.warningList
	{
		margin: 8px 0 0 0;
		padding-left: 22px;
		color: #7A5200;
	}

	.patchGrid
	{
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(420px, 1fr));
		gap: 12px;
	}

	.patchCard
	{
		border: 1px solid #E0E0E0;
		border-radius: 4px;
		padding: 10px 12px;
		background-color: #FAFAFA;
	}

		.patchCard.patchCardBusy
		{
			border-color: #7FAEE8;
			box-shadow: inset 0 0 0 1px #7FAEE8;
			background-color: #F7FAFF;
		}

		.patchCard.patchCardActiveDb
		{
			background-color: #F4F4F4;
		}

	.patchHead
	{
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 8px;
		margin-bottom: 2px;
	}

	.roleTag
	{
		text-transform: uppercase;
		letter-spacing: 0.08em;
		font-size: 0.8em;
		color: #666666;
		font-weight: bold;
	}

	.serverName
	{
		font-weight: bold;
		font-size: 1.15em;
	}

	.hostLine
	{
		font-size: 0.9em;
		margin-bottom: 4px;
	}

	.kvGrid
	{
		display: grid;
		grid-template-columns: max-content 1fr;
		gap: 4px 14px;
		align-items: baseline;
		margin: 6px 0;
	}

	.kvKey
	{
		font-weight: bold;
		color: #444444;
		white-space: nowrap;
	}

	.statusDot
	{
		flex: 0 0 auto;
		display: inline-block;
		width: 10px;
		height: 10px;
		border-radius: 50%;
		cursor: help;
		margin-right: 6px;
	}

	.dotGreen
	{
		background-color: #2E9E2E;
	}

	.dotAmber
	{
		background-color: #E0B93F;
	}

	.dotRed
	{
		background-color: #CC3333;
	}

	.dotGray
	{
		background-color: #AAAAAA;
	}

	.stateBadge
	{
		display: inline-block;
		padding: 2px 10px;
		border-radius: 10px;
		font-weight: bold;
		font-size: 0.9em;
		white-space: nowrap;
		cursor: help;
		margin-right: 4px;
	}

	.badgeGood
	{
		background-color: #D4F5D4;
		color: #0A5A0A;
		border: 1px solid #77CC77;
	}

	.badgeWarn
	{
		background-color: #FFEFC9;
		color: #7A5200;
		border: 1px solid #E0B93F;
	}

	.badgeBad
	{
		background-color: #FFE0E0;
		color: #8A0000;
		border: 1px solid #CC6666;
	}

	.badgeInfo
	{
		background-color: #DDEBFF;
		color: #123F7A;
		border: 1px solid #7FAEE8;
	}

	.badgeOff
	{
		background-color: #EEEEEE;
		color: #666666;
		border: 1px solid #CCCCCC;
	}

	.badgeActive
	{
		background-color: #3A7BD5;
		color: #FFFFFF;
		border: 1px solid #1F5AA8;
	}

	.jobBlock
	{
		margin-top: 10px;
		padding: 8px 10px;
		border: 1px solid #E0E0E0;
		border-radius: 4px;
		background-color: #FFFFFF;
	}

		.jobBlock.jobActive
		{
			border-color: #7FAEE8;
		}

	.jobHead
	{
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 6px 10px;
	}

	.jobTitle
	{
		font-size: 0.95em;
	}

	.progressLine
	{
		display: flex;
		align-items: center;
		gap: 8px;
		margin-top: 6px;
		font-weight: bold;
	}

	.pulseDot
	{
		flex: 0 0 auto;
		display: inline-block;
		width: 9px;
		height: 9px;
		border-radius: 50%;
		background-color: #3A7BD5;
		animation: patchPulse 1.2s ease-in-out infinite;
	}

	@keyframes patchPulse
	{
		0%, 100%
		{
			opacity: 1;
		}

		50%
		{
			opacity: 0.25;
		}
	}

	.deadlineLine,
	.logLine
	{
		font-size: 0.85em;
		margin-top: 4px;
	}

	.summaryLine
	{
		margin-top: 6px;
	}

	.updatesBlock
	{
		margin-top: 8px;
	}

	.tableScroll
	{
		overflow-x: auto;
		margin-top: 4px;
	}

	.updateTable
	{
		border-collapse: collapse;
		width: 100%;
		font-size: 0.9em;
	}

		.updateTable th,
		.updateTable td
		{
			text-align: left;
			padding: 3px 8px;
			border-bottom: 1px solid #EEEEEE;
			vertical-align: top;
		}

		.updateTable th
		{
			color: #555555;
			font-size: 0.9em;
		}

	.updateDetail
	{
		font-size: 0.9em;
	}

	.nowrap
	{
		white-space: nowrap;
	}

	.outcomeGood
	{
		color: #0A5A0A;
		font-weight: bold;
	}

	.outcomeBad
	{
		color: #8A0000;
		font-weight: bold;
	}

	.outcomeWarn
	{
		color: #7A5200;
	}

	.outcomeMuted
	{
		color: #888888;
	}

	.startBlock
	{
		margin-top: 10px;
		padding-top: 8px;
		border-top: 1px solid #EEEEEE;
	}

	.startNote
	{
		color: #555555;
		font-size: 0.9em;
		margin-top: 6px;
	}

	.disabledReason
	{
		color: #7A5200;
		font-size: 0.95em;
		font-style: italic;
	}

	.loadingInline
	{
		display: inline-flex;
		align-items: center;
		gap: 10px;
	}

	.linkButton
	{
		background: none;
		border: none;
		padding: 0;
		color: #1A5FB4;
		text-decoration: underline;
		cursor: pointer;
		font: inherit;
		font-size: 0.9em;
	}

	.policyLine
	{
		margin-top: 10px;
		font-size: 0.85em;
	}

	code.inline
	{
		font-family: Consolas, monospace;
		background-color: #F4F4F4;
		border-radius: 3px;
		padding: 0px 5px;
	}

	.slideRed :deep(.slideunlock)
	{
		--su-color-bg: #F5DCDC;
		--su-color-progress-normal-bg: #E5AFAF;
		--su-color-progress-complete-bg: #CC3333;
		margin: 0;
	}
</style>
